import * as React from "react";
import { Card, CircularProgress, Stack, Typography } from "@mui/joy";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";

import { fetchQuoteOfTheDay, fetchRandomQuote } from "../../api";
import type { PublicQuote } from "../../api";
import { formatISTDate } from "../../utils/date";

export function QuotePanel() {
  const [quote, setQuote] = React.useState<PublicQuote | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      try {
        let todays = await fetchQuoteOfTheDay().catch(() => null);
        if (!todays) {
          todays = await fetchRandomQuote().catch(() => null);
        }
        if (isMounted) {
          setQuote(todays ?? null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const devotionalQuote = quote?.text?.trim() || "By sincere chanting and dedicated seva, the heart becomes a temple of Sri Krishna.";
  const devotionalAuthor = quote?.author?.trim() || "Srila Prabhupada";
  const devotionalDate = quote?.date ? formatISTDate(quote.date) : null;

  return (
    <Card
      variant="soft"
      color="primary"
      sx={{ borderRadius: "xl", p: 3, gap: 1.4, boxShadow: "0 18px 40px rgba(13, 109, 219, 0.22)" }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <FormatQuoteRoundedIcon />
        <Typography level="title-md" sx={{ fontWeight: 700 }}>
          Daily meditation
        </Typography>
      </Stack>
      {loading ? (
        <Stack spacing={1} alignItems="flex-start" sx={{ py: 1 }}>
          <CircularProgress size="sm" color="neutral" />
          <Typography level="body-sm" sx={{ opacity: 0.8 }}>
            Gathering today’s reflection…
          </Typography>
        </Stack>
      ) : (
        <>
          <Typography level="body-lg">“{devotionalQuote}”</Typography>
          <Typography level="body-sm" sx={{ opacity: 0.75 }}>
            — {devotionalAuthor}
          </Typography>
          {devotionalDate ? (
            <Typography level="body-xs" sx={{ opacity: 0.65 }}>
              Featured for {devotionalDate}
            </Typography>
          ) : null}
        </>
      )}
    </Card>
  );
}
